import React from 'react';
import { Empty, type TableProps } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppButton, AppPagination, AppTable } from '@/components/atoms';
import { EditFill, EllipsisH } from '@/components/atoms/AppIcon';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import type { LatlonCacheListItem } from '@/services/admin/latlon-caches';
import { EMPTY_TEXT_FILTERED } from '@/constants';

type LatlonCachesMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type LatlonCachesTableSectionProps = {
  rows: LatlonCacheListItem[];
  loading: boolean;
  meta: LatlonCachesMeta;
  defaultPerPage: number;
  /** Show サイト区分 column for super-admin or family admin */
  canUseSiteTypeFeatures?: boolean;
  tenantNameBySiteType?: Map<string, string>;
  sortInfo: {
    columnKey?: string;
    order?: 'ascend' | 'descend';
  };
  onSort: (sorter: SorterResult<LatlonCacheListItem> | SorterResult<LatlonCacheListItem>[]) => void;
  onChangePage: (page: number) => void;
};

const LatlonCachesTableSection: React.FC<LatlonCachesTableSectionProps> = ({
  rows,
  loading,
  meta,
  defaultPerPage,
  canUseSiteTypeFeatures = false,
  tenantNameBySiteType = new Map(),
  sortInfo,
  onSort,
  onChangePage,
}) => {
  const { colors } = useTheme();
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'super-admin';
  const navigate = useNavigate();
  const location = useLocation();

  const columns: TableProps<LatlonCacheListItem>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'id' ? sortInfo.order : undefined,
      width: 80,
    },
    {
      title: '治療院ID',
      dataIndex: 'clinic_number',
      key: 'clinic_number',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'clinic_number' ? sortInfo.order : undefined,
      width: 120,
    },
    {
      title: '住所',
      dataIndex: 'address',
      key: 'address',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'address' ? sortInfo.order : undefined,
      width: 300,
      render: (_: string | null, record: LatlonCacheListItem) => (
        <div>
          <div>{record.address}</div>
          {record.address_geop && (
            <div className="text-gray-500 text-xs">GeOAP: {record.address_geop}</div>
          )}
        </div>
      ),
    },
    {
      title: 'サイト区分',
      dataIndex: 'site_type',
      key: 'site_type',
      hidden: !canUseSiteTypeFeatures,
      render: (siteType: string | null) => {
        if (!siteType) return '';
        return tenantNameBySiteType.get(siteType) ?? siteType;
      },
      width: 120,
    },
    {
      title: '緯度',
      dataIndex: 'latitude',
      key: 'latitude',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'latitude' ? sortInfo.order : undefined,
      width: 180,
    },
    {
      title: '経度',
      dataIndex: 'longitude',
      key: 'longitude',
      sorter: true,
      sortOrder: sortInfo.columnKey === 'longitude' ? sortInfo.order : undefined,
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <div className="flex gap-2">
          <AppButton
            icon={<EllipsisH />}
            type="primary"
            onClick={() =>
              navigate(`/${currentSite}/admin/latlon_caches/${record.id}`, {
                state: { listSearch: location.search },
              })
            }
          >
            表示
          </AppButton>
          <AppButton
            style={
              {
                backgroundColor: colors.secondary,
                ['--action-bg-hover' as string]: colors.secondaryHover,
              } as React.CSSProperties
            }
            className="hover:!bg-[var(--action-bg-hover)]"
            icon={<EditFill />}
            type="primary"
            onClick={() =>
              navigate(`/${currentSite}/admin/latlon_caches/${record.id}/edit`, {
                state: { listSearch: location.search },
              })
            }
          >
            編集
          </AppButton>
        </div>
      ),
      width: 180,
    },
  ];

  return (
    <div className="bg-white rounded-md border">
      <AppTable
        isAdminSite
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        onChange={(_pagination, _filters, sorter) =>
          onSort(sorter as SorterResult<LatlonCacheListItem> | SorterResult<LatlonCacheListItem>[])
        }
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT_FILTERED} />
          ),
        }}
        scroll={{ x: 'max-content' }}
      />
      {meta && (
        <div className="p-4 flex justify-end">
          <AppPagination
            current={meta.current_page}
            total={meta.total}
            pageSize={meta.per_page || defaultPerPage}
            hideOnSinglePage
            onChange={p => onChangePage(p)}
          />
        </div>
      )}
    </div>
  );
};

export default LatlonCachesTableSection;
